import { ColumnsType } from "antd/es/table";
import { Switch, Spin } from "antd";
import { CustomIcon } from "../../components/reusable/CustomIcon";
import StyledDropdown from "src/components/reusable/StyledDropdown";

export interface User {
  id: number;
  name: string;
  email: string;
  first_name: string;
  last_name: string;
  accessLevel: string;
  isActive: boolean;
  dashboardAccess?: string[];
  last_login: string | null;
  socialListeningAccess?: boolean;
}

export interface UserManagementProps {
  user_id?: string;
  tenant_id?: string;
  role_id?: string;
  api_key?: string;
}

export interface UserFormValues {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  role: string;
  features?: string[];
}

export interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void; // Callback to close the modal
  addUser: (newUser: User) => void; // Add User callback
  tenant_id: string; // Tenant ID
  user_id: string; // User ID
  api_key: string; // API Key
  isSocialListeningEnabled: boolean;
}

export interface Dashboard {
  dashboard_id: number;
  dashboard_name: string;
  dashboard_url: string;
}

export interface DashboardOption {
  dashboard_id: number;
  dashboard_name: string;
  tick_mark: boolean;
  name?: string; // For MultiSelect compatibility
  value?: string; // For MultiSelect compatibility
  assigned?: boolean; // For tracking if the dashboard is assigned
}

export interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onUpdateUser: (updatedUser: User) => void;
  isSocialListeningEnabled: boolean;
}

export const getUserTableColumns = (
  handleAccessLevelChange: (userId: number, accessLevel: string) => void,
  toggleStatus: (userId: number) => void,
  handleEditUser: (user: User) => void,
  handleDeleteClick: (user: User) => void,
  deletingUserId: number | null,
  t: (key: string, options?: Record<string, unknown>) => string,
  isArabic = false
): ColumnsType<User> => [
  {
    title: t("userManagement.table.name"),
    dataIndex: "name",
    key: "name",
    align: isArabic ? "center" : undefined,
    render: (text: string) => <span className="campaign-name">{text}</span>,
  },
  {
    title: t("userManagement.table.email"),
    dataIndex: "email",
    key: "email",
    align: isArabic ? "center" : undefined,
  },
  {
    title: t("userManagement.table.accessLevel"),
    dataIndex: "accessLevel",
    key: "accessLevel",
    align: isArabic ? "center" : undefined,
    render: (_, user) => {
      // Capitalize for display
      const capitalized = user.accessLevel
        ? user.accessLevel.charAt(0).toUpperCase() +
          user.accessLevel.slice(1).toLowerCase()
        : "";
      const isDisabled = user.accessLevel === "Admin" || user.last_login === null;

      return (
        <div className="access-level-cell-inner">
          <StyledDropdown
            value={capitalized}
            showSearch={false}
            popupClassName="custom-dropdown-menu"
            placement="bottomLeft"
            getPopupContainer={triggerNode =>
              triggerNode?.ownerDocument?.body || document.body
            }
            onChange={value =>
              !isDisabled &&
              handleAccessLevelChange(user.id, value as string)
            }
            disabled={isDisabled}
            className={`access-level-select ${
              isDisabled ? "access-level-select--disabled" : ""
            }`}
            options={[
              { label: t("userManagement.role.viewer"), value: "Viewer" },
              { label: t("userManagement.role.admin"), value: "Admin" },
            ]}
          />
        </div>
      );
    },
  },

  {
    title: t("userManagement.table.status"),
    dataIndex: "isActive",
    key: "status",
    align: isArabic ? "center" : undefined,
    render: (_, user) => (
      <div>
        <Switch
          className="user-management-switch"
          checked={user.isActive}
          onChange={() => toggleStatus(user.id)}
          disabled={user.accessLevel === "Admin" || user.last_login === null}
        />
        <span style={isArabic ? { marginRight: 8 } : { marginLeft: 8 }}>
          {user.isActive
            ? t("userManagement.status.active")
            : t("userManagement.status.inactive")}
        </span>
      </div>
    ),
  },
  {
    title: t("userManagement.table.actions"),
    key: "actions",
    align: isArabic ? "center" : undefined,
    render: (_, user) => (
      <div
        className="user-management-icons"
        style={{ display: "flex", gap: "8px", justifyContent: isArabic ? "center" : "flex-start" }}
      >
        {/* ✅ Edit Icon */}
        {user.accessLevel === "Admin" || user.last_login === null ? (
          <CustomIcon
            name="edit"
            tooltip={t("userManagement.actions.edit")}
            size={18}
            color="#141B34"
            style={{ opacity: 0.5, cursor: "not-allowed" }}
          />
        ) : (
          <CustomIcon
            name="edit"
            tooltip={t("userManagement.actions.edit")}
            size={18}
            color="#141B34"
            onClick={() => handleEditUser(user)}
          />
        )}

        {/* ✅ Delete Icon */}
        {deletingUserId === user.id ? (
          <Spin size="small" />
        ) : (
          <>
            {user.accessLevel === "Admin" || user.last_login === null ? (
              <CustomIcon
                name="delete"
                tooltip={t("userManagement.actions.delete")}
                size={18}
                color="#141B34"
                style={{ opacity: 0.5, cursor: "not-allowed" }}
              />
            ) : (
              <CustomIcon
                name="delete"
                tooltip={t("userManagement.actions.delete")}
                size={18}
                color="#141B34"
                onClick={() => handleDeleteClick(user)}
              />
            )}
          </>
        )}
      </div>
    ),
  },
];
